// src/services/receitaService.js
// Serviço para operações de receitas financeiras (Entrada Financeira)

import api from './api';

// Buscar todas as receitas com filtros opcionais
export const fetchReceitas = async (filters = {}) => {
  try {
    const response = await api.get('/entrada-financeira', { params: filters });
    
    // Retornar estrutura completa com dados e paginação
    if (response.data && response.data.success && response.data.data) {
      return {
        entradas: Array.isArray(response.data.data.entradas) ? response.data.data.entradas : [],
        pagination: response.data.data.pagination || {
          total: 0,
          totalPages: 0,
          page: 1,
          limit: 10
        }
      };
    } else if (response.data && Array.isArray(response.data.data)) {
      return {
        entradas: response.data.data,
        pagination: {
          total: response.data.data.length,
          totalPages: 1,
          page: 1,
          limit: response.data.data.length
        }
      };
    } else if (Array.isArray(response.data)) {
      return {
        entradas: response.data,
        pagination: {
          total: response.data.length,
          totalPages: 1,
          page: 1,
          limit: response.data.length
        }
      };
    } else {
      return {
        entradas: [],
        pagination: {
          total: 0,
          totalPages: 0,
          page: 1,
          limit: 10
        }
      };
    }
  } catch (error) {
    console.error('Erro ao buscar receitas:', error);
    return {
      entradas: [],
      pagination: {
        total: 0,
        totalPages: 0,
        page: 1,
        limit: 10
      }
    };
  }
};

// Buscar receita por ID
export const fetchReceitaById = async (id) => {
  try {
    const response = await api.get(`/entrada-financeira/${id}`);
    
    let receitaData;
    if (response.data && response.data.success) {
      receitaData = response.data.data;
    } else if (response.data) {
      receitaData = response.data;
    } else {
      throw new Error('Receita não encontrada');
    }
    
    // Log para depurar o formato da data retornada pelo backend
    console.log('Dados da receita retornados pelo backend:', receitaData);
    console.log('Campo dataBalanco retornado:', receitaData.dataBalanco, typeof receitaData.dataBalanco);
    
    return receitaData;
  } catch (error) {
    console.error('Erro ao buscar receita:', error);
    throw new Error(error.response?.data?.message || 'Erro ao buscar receita');
  }
};

// Criar nova receita
export const createReceita = async (receitaData) => {
  try {
    const response = await api.post('/entrada-financeira', receitaData);
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else if (response.data) {
      return response.data;
    } else {
      throw new Error('Erro ao criar receita');
    }
  } catch (error) {
    console.error('Erro ao criar receita:', error);
    throw new Error(error.response?.data?.message || 'Erro ao criar receita');
  }
};

// Atualizar receita existente
export const updateReceita = async (id, receitaData) => {
  try {
    const response = await api.put(`/entrada-financeira/${id}`, receitaData);
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else if (response.data) {
      return response.data;
    } else {
      throw new Error('Erro ao atualizar receita');
    }
  } catch (error) {
    console.error('Erro ao atualizar receita:', error);
    throw new Error(error.response?.data?.message || 'Erro ao atualizar receita');
  }
};

// Deletar receita
export const deleteReceita = async (id) => {
  try {
    const response = await api.delete(`/entrada-financeira/${id}`);
    
    if (response.data && response.data.success) {
      return response.data;
    } else if (response.data) {
      return response.data;
    } else {
      return { success: true, message: 'Receita deletada com sucesso' };
    }
  } catch (error) {
    console.error('Erro ao deletar receita:', error);
    throw new Error(error.response?.data?.message || 'Erro ao deletar receita');
  }
};

// Buscar opções para dropdowns
export const fetchContasReceita = async () => {
  try {
  const response = await api.get('/contas/all');
    console.log('Contas retornadas:', response);
    const raw = response.data;

    // Normalizar possíveis formatos de retorno da API
    // Possíveis formatos:
    // - Array direto: response.data = [ { id, descricao }, ... ]
    // - response.data = { contas: [...] }
    // - response.data = { success: true, data: { contas: [...] } }
    // - response.data = { success: true, data: [...] }
    let contasArray = [];

    if (!raw) {
      contasArray = [];
    } else if (Array.isArray(raw)) {
      contasArray = raw;
    } else if (Array.isArray(raw.contas)) {
      contasArray = raw.contas;
    } else if (raw.success && raw.data) {
      if (Array.isArray(raw.data.contas)) contasArray = raw.data.contas;
      else if (Array.isArray(raw.data)) contasArray = raw.data;
    } else if (Array.isArray(raw.data)) {
      contasArray = raw.data;
    } else {
      contasArray = [];
    }

    console.log('Contas normalizadas para frontend:', contasArray);

    return contasArray.map(conta => ({
      id: conta.id,
      descricao: conta.descricao || conta.nome || conta.label || `Conta ${conta.id}`
    }));
  } catch (error) {
    console.error('Erro ao buscar contas:', error);
    return [];
  }
};

// Buscar pessoas para dropdown
export const fetchPessoas = async () => {
  try {
    const response = await api.get('/pessoas');
    
    if (response.data.success) {
      return response.data.data.map(pessoa => ({
        id: pessoa.id,
        nome: pessoa.nome,
        email: pessoa.email,
        dtype: pessoa.dtype
      }));
    } else {
      return [];
    }
  } catch (error) {
    console.error('Erro ao buscar pessoas:', error);
    return [];
  }
};

// Buscar pedidos para dropdown
export const fetchPedidos = async () => {
  try {
    const response = await api.get('/pedidos');
    
    if (response.data.success) {
      return response.data.data.map(pedido => ({
        id: pedido.id,
        descricao: pedido.descricao || `Pedido #${pedido.id}`,
        pessoaNome: pedido.pessoaNome,
        valor: pedido.valor
      }));
    } else {
      return [];
    }
  } catch (error) {
    console.error('Erro ao buscar pedidos:', error);
    return [];
  }
};

// Buscar TODAS as receitas para exportação (sem paginação), respeitando os filtros
export const fetchReceitasExport = async (filters = {}) => {
  try {
    // 1ª requisição: busca 1 registro apenas para obter o total
    const countResponse = await api.get('/entrada-financeira', {
      params: { ...filters, page: 1, limit: 1 }
    });

    const total =
      countResponse.data?.data?.pagination?.total ||
      countResponse.data?.pagination?.total ||
      0;

    if (total === 0) return [];

    // 2ª requisição: busca todos os registros usando o total real, ordenados por dataBalanco ASC
    const response = await api.get('/entrada-financeira', {
      params: { ...filters, page: 1, limit: total, orderBy: 'dataBalanco', orderDir: 'ASC' }
    });

    if (response.data && response.data.success && response.data.data) {
      return Array.isArray(response.data.data.entradas)
        ? response.data.data.entradas
        : [];
    }
    return [];
  } catch (error) {
    console.error('Erro ao buscar receitas para exportação:', error);
    return [];
  }
};

// Estatísticas básicas
export const fetchReceitasStats = async () => {
  try {
    const response = await api.get('/entrada-financeira/stats');
    
    if (response.data && response.data.success && response.data.data) {
      return response.data.data;
    } else {
      return {
        total: 0,
        count: 0,
        average: 0,
        porConta: {}
      };
    }
  } catch (error) {
    console.error('Erro ao buscar estatísticas:', error);
    // Retornar estrutura padrão em caso de erro
    return {
      total: 0,
      count: 0,
      average: 0,
      porConta: {}
    };
  }
};

const receitaService = {
  fetchReceitas,
  fetchReceitasExport,
  fetchReceitaById,
  createReceita,
  updateReceita,
  deleteReceita,
  fetchContasReceita,
  fetchPessoas,
  fetchPedidos,
  fetchReceitasStats
};

export default receitaService;